import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { validatorHopStallSchema } from "@prospect/shared";

import { isValidatorAgent, useAgents } from "@/api/agents";
import { useJob, useRetryValidator, useRunCall } from "@/api/jobs";
import { useModels } from "@/api/models";
import type {
  AuditLogEntry,
  JobCostSummary,
  JobDetailResponse,
  JobReviewSummary,
  JobStatus,
  RunCallDetailResponse,
  RunLogEntry,
} from "@/api/types";
import { useRunStream } from "@/api/use-run-stream";
import { AbortRunButton } from "@/components/AbortRunButton";
import {
  AgentConfigSnapshot,
  filterSnapshotToSection,
  type SnapshotPanel,
} from "@/components/AgentConfigSnapshot";
import { ClearOrphanDraftsBanner } from "@/components/ClearOrphanDraftsBanner";
import { ErrorBanner } from "@/components/ErrorBanner";
import { ModeProgressPills } from "@/components/ModeProgressPills";
import { RelativeTime } from "@/components/RelativeTime";
import { SendDraftsToValidationBanner } from "@/components/SendDraftsToValidationBanner";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  compact,
  diffMsFromIso,
  formatDurationMs,
  formatJobNumber,
  ms,
  usd,
} from "@/lib/format";

const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set([
  "completed",
  "failed",
  "expired",
  "cancelled",
]);

interface AuditTimelineRow {
  source: "audit";
  /** ISO timestamp used for chronological ordering. */
  timestamp: string;
  key: string;
  audit: AuditLogEntry;
}

interface RunTimelineRow {
  source: "run";
  timestamp: string;
  key: string;
  run: RunLogEntry;
}

type TimelineRow = AuditTimelineRow | RunTimelineRow;

/** Stable per-call key — `agentType:callIndex` collides only on a single
 * paired-agent run if both agents share an `agent_type`, which the schema
 * forbids. Used both as React `key` and as the expanded-set token. */
function runRowKey(run: { agentType: string; callIndex: number }): string {
  return `${run.agentType}:${run.callIndex}`;
}

/**
 * Merge `audit_log` + `run_log` into a single chronological timeline. Audit
 * events are keyed by `createdAt`; run events are keyed by `startedAt`
 * (when the LLM call kicked off — that's the right anchor on the user's
 * mental timeline; `completedAt` would push long calls down the page).
 */
export function buildTimeline(audit: AuditLogEntry[], run: RunLogEntry[]): TimelineRow[] {
  const rows: TimelineRow[] = [];
  for (const a of audit) {
    rows.push({ source: "audit", timestamp: a.createdAt, key: `a-${a.id}`, audit: a });
  }
  for (const r of run) {
    rows.push({
      source: "run",
      timestamp: r.startedAt,
      key: `r-${runRowKey(r)}`,
      run: r,
    });
  }
  rows.sort((x, y) => x.timestamp.localeCompare(y.timestamp));
  return rows;
}

/**
 * Pluck the per-agent snapshot from a `workerResult` / `validatorResult`
 * payload — these have shape `{ artifacts: [{...}], callSummary: [...] }`
 * with `artifacts[0].agentConfigSnapshot` carrying the per-agent
 * post-Zod freeze (see `packages/shared/src/schemas/curator-artifacts.ts`).
 * Returns `null` when the artifact is missing or its snapshot isn't a
 * plain `(string|number)` map — the JSON shape is opaque on the SPA
 * side so we narrow defensively.
 *
 * Exported for unit testing.
 */
export function extractArtifactSnapshot(
  result: unknown,
): Record<string, string | number> | null {
  if (!result || typeof result !== "object") return null;
  const artifacts = (result as { artifacts?: unknown }).artifacts;
  if (!Array.isArray(artifacts) || artifacts.length === 0) return null;
  const first = artifacts[0];
  if (!first || typeof first !== "object") return null;
  const snapshot = (first as { agentConfigSnapshot?: unknown }).agentConfigSnapshot;
  return narrowSnapshot(snapshot);
}

/**
 * Narrow an `unknown` JSON value to the snapshot shape — a flat object
 * whose values are strings or numbers (the only Sheets cell types).
 * Returns `null` if the shape doesn't match. Tolerant: drops any keys
 * whose value is the wrong type rather than failing the whole parse,
 * so a stray boolean from an old artifact doesn't blank the panel.
 */
function narrowSnapshot(value: unknown): Record<string, string | number> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === "string" || typeof v === "number") out[k] = v;
  }
  return out;
}

/**
 * Build the up-to-three category panels rendered on JobDetail. Each
 * panel surfaces one section of the metadata taxonomy, sourced from
 * whichever agent's snapshot owns those keys:
 *
 * - **Curator** panel (Curator section keys) — sourced from the
 *   curator's snapshot. Artifact post-Zod preferred (most accurate
 *   after coercion + defaults gap-fill); falls back to
 *   `jobs.configSnapshot` (the dispatch-time raw freeze) when the
 *   curator hasn't finished yet or its callback never landed.
 * - **Runtime** panel (Runtime section keys) — also sourced from
 *   the curator's snapshot. Runtime is curator-runtime per the
 *   metadata, so a validator-only catch-up run has no Runtime panel.
 * - **Validator** panel (Validator section keys) — sourced from the
 *   validator's snapshot. Artifact post-Zod preferred; falls back to
 *   the `validator_hop_dispatched` audit row's `configSnapshot`
 *   payload so the panel renders the moment the hop dispatches,
 *   before the validator's own post-Zod artifact lands.
 *
 * Empty panels are dropped — for a curator-only run the Validator
 * panel hides; for a validator catch-up the Curator and Runtime
 * panels hide.
 *
 * Cost / Notifications / Catalogue keys never render here — they're
 * global config. Operator-added unknown keys also don't render;
 * they're visible on `/settings` but irrelevant per-run.
 *
 * Exported for unit testing.
 */
export function extractCategoryPanels(job: JobDetailResponse): SnapshotPanel[] {
  // Curator-side: artifact post-Zod preferred, else dispatch freeze.
  const curatorSnapshot =
    extractArtifactSnapshot(job.workerResult) ??
    narrowSnapshot(job.configSnapshot) ??
    {};
  // Validator-side: artifact post-Zod preferred, else
  // `validator_hop_dispatched` audit-row freeze.
  const validatorSnapshot =
    extractArtifactSnapshot(job.validatorResult) ??
    validatorSnapshotFromAudit(job.auditLog) ??
    {};

  // The three panels always come from the same two snapshots in the
  // same order — encoding that as a small spec table makes the shape
  // obvious at a glance and means adding a fourth panel (e.g. a
  // future Consultant section) is one row, not a fourth near-identical
  // copy of the same six lines below.
  const specs: ReadonlyArray<{
    section: SnapshotPanel["section"];
    title: string;
    source: Record<string, string | number>;
  }> = [
    { section: "curator", title: "Curator", source: curatorSnapshot },
    { section: "runtime", title: "Runtime", source: curatorSnapshot },
    { section: "validator", title: "Validator", source: validatorSnapshot },
  ];

  const panels: SnapshotPanel[] = [];
  for (const spec of specs) {
    const rows = filterSnapshotToSection(spec.source, spec.section);
    if (rows.length > 0) {
      panels.push({ section: spec.section, title: spec.title, rows });
    }
  }
  return panels;
}

/**
 * Pluck the validator's dispatch-time freeze off the
 * `validator_hop_dispatched` audit row's payload. This is the
 * orchestrator-side symmetric counterpart to `jobs.configSnapshot`
 * (which only carries the curator-time freeze). Lets the validator
 * panel render the moment the hop dispatches, before the validator's
 * own post-Zod artifact lands. Returns `null` when the audit row is
 * absent (legacy jobs predating the field) or its `configSnapshot`
 * payload doesn't narrow.
 */
function validatorSnapshotFromAudit(
  auditLog: AuditLogEntry[],
): Record<string, string | number> | null {
  for (const entry of auditLog) {
    if (entry.eventType !== "validator_hop_dispatched") continue;
    const payload = entry.payload as { configSnapshot?: unknown } | null;
    const narrowed = narrowSnapshot(payload?.configSnapshot);
    if (narrowed && Object.keys(narrowed).length > 0) return narrowed;
  }
  return null;
}

/**
 * One-line "what is this run for" summary rendered on the job header.
 * Switches on `agentType` so each agent gets a description that
 * matches what it does — the curator "1–2 tools across CRM…" text
 * used to leak into every non-curator card (issue #575, finding #6).
 *
 * `agentLabel` is the friendly display name (e.g. "DB Curator")
 * resolved from the `useAgents` lookup; callers fall back to the raw
 * `agent_type` when the lookup misses.
 *
 * Recognised `agentType` shapes:
 * - `db_curator` — reads curator context (mode) + snapshot ranges.
 * - `*_validator` (e.g. `db_curator_validator`) — catch-up scoring
 *   against the sibling curator's drafts.
 * - `intake-validator` — M2, judges a memo against the intake rubric.
 * - `match-validator` — M2, validates Analyst recommendations.
 * - `analyst` — M2, produces the solution presentation.
 * Anything else falls back to the bare "A {agentLabel} run." shape.
 *
 * Exported for unit testing.
 */
export function buildJobSummary(
  agentType: string,
  agentLabel: string,
  context: unknown,
  configSnapshot: unknown,
): string {
  const ctx = (context && typeof context === "object" ? context : {}) as Record<
    string,
    unknown
  >;
  const config = narrowSnapshot(configSnapshot) ?? {};

  if (isValidatorAgent(agentType)) {
    const cap = config.validator_max_rows_per_run;
    if (cap !== undefined) {
      return `A ${agentLabel} catch-up scoring up to ${cap} unrated drafts.`;
    }
    return `A ${agentLabel} catch-up scoring unrated drafts.`;
  }

  if (agentType === "intake-validator") {
    return `An ${agentLabel} run judging a memo against the intake rubric.`;
  }

  if (agentType === "match-validator") {
    return `A ${agentLabel} run validating the Analyst's recommendations against the memo objectives.`;
  }

  if (agentType === "analyst") {
    return `An ${agentLabel} run producing a solution presentation from a validated memo.`;
  }

  if (agentType === "db_curator") {
    // Curator-side. The two known shapes today:
    //  - `mode: "autonomous"` → range + categories.
    //  - `mode: "targeted", toolNames: string[]` → count of named tools.
    const mode = typeof ctx.mode === "string" ? ctx.mode : null;
    if (mode === "targeted") {
      const toolNames = Array.isArray(ctx.toolNames) ? ctx.toolNames : [];
      return `A ${agentLabel} run targeting ${toolNames.length} specific tool${
        toolNames.length === 1 ? "" : "s"
      }.`;
    }
    const min = config.min_new_drafts_per_run;
    const max = config.max_new_drafts_per_run;
    const categories = typeof config.categories === "string" ? config.categories : "";
    if (min !== undefined && max !== undefined && categories.length > 0) {
      return `A ${agentLabel} run looking for ${min}–${max} tools across ${categories}.`;
    }
  }

  return `A ${agentLabel} run.`;
}

/** Render the JSON-shaped `lastError` payload as a single readable line.
 * Strings pass through; everything else is JSON.stringified so a structured
 * error blob doesn't bloat the page. */
function renderLastError(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Narrow `lastError` to the validator-hop stall marker the orchestrator
 * writes via `markValidatorHopStalled` (see
 * `services/orchestrator/src/routes/callbacks.ts`). Returns the human
 * reason string when the shape matches, else `null`. Used to gate the
 * amber "stalled" indicator and the "Retry validator" banner — both
 * fire iff `status === "validating"` AND this helper returns non-null.
 *
 * Exported for unit testing.
 */
export function parseValidatorHopStall(value: unknown): { reason: string } | null {
  const parsed = validatorHopStallSchema.safeParse(value);
  return parsed.success ? { reason: parsed.data.reason } : null;
}

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useJob(id);
  const { agentsByType } = useAgents();

  // Canonicalise the URL to the friendly form once the job loads.
  // Operators landing on `/jobs/<uuid>` (legacy bookmarks, audit-log
  // links) get a `replace` redirect to `/jobs/<padded-number>` — no
  // back-button entry is added, the URL just flips silently. The
  // effect is gated on `query.data` so it fires after the orchestrator
  // resolves whichever shape the URL carried; UUID and friendly
  // forms otherwise behave identically downstream.
  const canonicalForm = query.data ? formatJobNumber(query.data.jobNumber) : null;
  useEffect(() => {
    if (!id || !canonicalForm) return;
    if (id === canonicalForm) return;
    // Only flip when the URL actually carries a non-canonical shape —
    // skip the no-op self-replace that React StrictMode would
    // otherwise trigger.
    navigate(`/jobs/${canonicalForm}`, { replace: true });
  }, [id, canonicalForm, navigate]);

  // Live stream — open while the job is non-terminal. The hook
  // short-circuits when `disabled`, so a completed job pays no SSE
  // cost. Each new event triggers a `useJob` refetch so the canonical
  // Timeline section grows incrementally as state advances, instead of
  // waiting for the 5-second polling tick. The dedicated "live"
  // indicator next to the status badge is the only visible affordance
  // for "this is happening now" — duplicating the events into a
  // separate panel was ambiguous (same data, different render) and
  // confusing because the panel disappeared on terminal status.
  const isTerminal = query.data ? TERMINAL_STATUSES.has(query.data.status) : false;
  const stream = useRunStream(id, { disabled: !id || isTerminal });
  const refetch = query.refetch;
  const eventCount = stream.events.length;
  const { terminalStatus } = stream;
  useEffect(() => {
    if (eventCount > 0) {
      void refetch();
    }
  }, [eventCount, refetch]);
  useEffect(() => {
    if (terminalStatus) {
      void refetch();
    }
  }, [terminalStatus, refetch]);

  // Retry-validator mutation — fired by the banner button when a job
  // has stalled on the worker→validator hop. The hook invalidates the
  // per-job query on success so the banner clears as soon as the
  // re-dispatch lands.
  const retryValidator = useRetryValidator();

  // Per-row expand state for the trace viewer. Set of `agentType:callIndex`
  // tokens — only run-log rows are expandable, audit rows stay flat.
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(() => new Set());
  const toggleRun = (key: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (query.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  }

  const job = query.data;
  const timeline = buildTimeline(job.auditLog, job.runLog);
  // `stall` is non-null iff the validator hop bailed out and left the
  // job in `validating` with the `{ source: "validator_hop", reason }`
  // marker. Drives both the amber "stalled" indicator (in place of the
  // green "live" pulse) and the retry-validator banner below. We
  // additionally gate on `status === "validating"` so a stale marker
  // surfaced by a future code path can't trigger the banner on a job
  // that has moved on. */
  const stall = job.status === "validating" ? parseValidatorHopStall(job.lastError) : null;
  const lastErrorText = stall === null ? renderLastError(job.lastError) : "";
  const wallClockMs = diffMsFromIso(job.startedAt, job.completedAt);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/jobs" className="text-xs font-mono uppercase tracking-label text-text-muted hover:text-text">
          ← Back to jobs
        </Link>
      </div>

      <Card>
        <CardHeader>
          <p className="label-mono">Job</p>
          <CardTitle
            className="font-mono text-lg break-all"
            title={job.id}
          >
            #{formatJobNumber(job.jobNumber)}
          </CardTitle>
          <CardDescription>
            <span className="text-text" title={job.agentType}>
              {agentsByType[job.agentType]?.name ?? job.agentType}
            </span>{" "}
            · created <RelativeTime date={job.createdAt} /> · updated{" "}
            <RelativeTime date={job.updatedAt} />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-text">
            {buildJobSummary(
              job.agentType,
              agentsByType[job.agentType]?.name ?? job.agentType,
              job.context,
              job.configSnapshot,
            )}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={job.status} />
            {/* `completed` covers both approve and reject HITL rollups
                post-#213 — the muted chip disambiguates at a glance.
                Skip on jobs that never reached HITL (`reviewDecision`
                is `null`); `reviewDecision === "expired"` jobs render
                the danger `expired` status badge directly so no chip
                is needed there either (the enum is wider than what
                this site renders by design — `JobWithTimeline`
                projects the full DB enum). */}
            {job.status === "completed" &&
            (job.reviewDecision === "approved" ||
              job.reviewDecision === "rejected") ? (
              <Badge variant="muted">{job.reviewDecision}</Badge>
            ) : null}
            {/* The dispatch-attempt chip is hidden in the overwhelmingly
                common success-on-first-try single-agent case
                (`attemptCount === 1 && validatorAttemptCount === null`) —
                a bare `attempt #1` read as visual noise / a possible bug
                rather than a reliability signal. It surfaces only when the
                value carries information: a retry happened, or a validator
                strand has run. Mandated by
                `docs/decisions/2026-05-10-attempt-count-strand-split.md`
                § Amendments (2026-06-17, #228). */}
            {job.attemptCount === 1 && job.validatorAttemptCount === null ? null : (
              <span
                className="text-xs text-text-muted font-mono"
                title="Dispatch attempts — incremented only when the dispatch HTTP call to the agent service fails and retries (max 4). Not the number of LLM turns."
              >
                {job.validatorAttemptCount !== null
                  ? `worker #${job.attemptCount} · validator #${job.validatorAttemptCount}`
                  : `attempt #${job.attemptCount}`}
              </span>
            )}
            {!isTerminal ? (
              stall !== null ? (
                <StalledIndicator />
              ) : (
                <LiveIndicator state={stream.connectionState} />
              )
            ) : null}
            {lastErrorText ? (
              <span className="text-xs text-red-300 font-mono break-all">
                last error: {lastErrorText}
              </span>
            ) : null}
            {/* Self-gates on `status === 'running'`; sits on the right
                via its own `ml-auto` so the existing badges + indicators
                stay left-aligned and unchanged on terminal jobs. */}
            <AbortRunButton jobId={job.id} status={job.status} />
          </div>
        </CardContent>
      </Card>

      {/* Stalled-validator-hop banner. Mirrors the `pending_review` CTA
          shape so the operator sees the recovery affordance immediately —
          a job stuck in `validating` with no banner used to look like
          it was still progressing because the live indicator turned
          green. Button POSTs to `/api/jobs/:id/retry-validator`; the
          mutation invalidates the per-job query so the banner clears
          on success. */}
      {stall !== null ? (
        <div className="flex items-center justify-between gap-3 rounded border border-amber-400/40 bg-amber-400/10 px-4 py-3">
          <div>
            <p className="text-sm text-text">
              Validator hop stalled — the validator did not pick this run up.
            </p>
            <p className="text-xs text-text-muted break-all">
              Reason: {stall.reason || "(unknown)"}
            </p>
            {retryValidator.isError ? (
              <p className="text-xs text-red-300 font-mono break-all">
                Retry failed:{" "}
                {retryValidator.error instanceof Error
                  ? retryValidator.error.message
                  : String(retryValidator.error)}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={retryValidator.isPending}
            onClick={() => retryValidator.mutate(job.id)}
            className="inline-flex h-9 items-center justify-center rounded bg-amber-400 px-4 text-sm font-medium text-bg hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {retryValidator.isPending ? "Retrying…" : "Retry validator"}
          </button>
        </div>
      ) : null}

      {/* Targeted-mode curator "send drafts through validation"
          affordance — issue #641. Sits above ClearOrphanDraftsBanner
          so the progression path reads as primary; both stay
          reachable. Self-gates on
          `completed + db_curator + worker_skipped_validator + unrated
          rows still in tools_draft`. */}
      <SendDraftsToValidationBanner
        jobId={job.id}
        jobStatus={job.status}
        agentType={job.agentType}
        auditLog={job.auditLog}
      />

      {/* Self-gated on the orchestrator's `canClear` predicate — see
          issue #158 and the component TSDoc. */}
      <ClearOrphanDraftsBanner jobId={job.id} />

      {/* `pending_review` CTA banner sits above Summary so it's the
          first thing an operator sees when a batch needs attention —
          on long-running paired-agent runs the cost block can push
          the CTA below the fold otherwise. */}
      {job.status === "pending_review" ? (
        <div className="flex items-center justify-between gap-3 rounded border border-accent/40 bg-accent-dim px-4 py-3">
          <div>
            <p className="text-sm text-text">This batch is awaiting review.</p>
            <p className="text-xs text-text-muted">
              Approve or reject each draft to commit the run.
            </p>
          </div>
          <Link
            to={`/jobs/${formatJobNumber(job.jobNumber)}/review`}
            className="inline-flex items-center justify-center rounded bg-accent px-4 text-sm font-medium text-bg hover:bg-accent/90 h-9"
          >
            Review batch →
          </Link>
        </div>
      ) : null}

      {/* Live mode-progress panel — curator-only, since `mode_progress`
          is the curator's vocabulary. Renders all-queued before the
          first event lands; advances on each forwarded `mode_progress`
          SSE row (live) and on snapshot replay for mid-run reload and
          terminal jobs (issue #240). */}
      {job.agentType === "db_curator" ? (
        <ModeProgressPills auditLog={job.auditLog} jobStatus={job.status} />
      ) : null}

      <JobSummaryCard
        cost={job.costSummary}
        review={job.reviewSummary}
        wallClockMs={wallClockMs}
      />

      {extractCategoryPanels(job).map((panel) => (
        <AgentConfigSnapshot key={panel.section} panel={panel} />
      ))}

      <section className="space-y-3">
        <h2 className="label-mono">Timeline</h2>
        {timeline.length === 0 ? (
          <p className="text-sm text-text-muted">No events recorded.</p>
        ) : (
          <ol className="space-y-2">
            {timeline.map((row) => {
              if (row.source === "audit") {
                return <AuditRow key={row.key} audit={row.audit} timestamp={row.timestamp} />;
              }
              const key = runRowKey(row.run);
              return (
                <RunRow
                  key={row.key}
                  jobId={job.id}
                  run={row.run}
                  timestamp={row.timestamp}
                  expanded={expandedRuns.has(key)}
                  onToggle={() => toggleRun(key)}
                />
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}

/**
 * Headline cost / token / latency / wall-clock / review block. Hides
 * sub-blocks that don't apply yet — `costSummary.callCount === 0` for
 * a job that hasn't dispatched, `reviewSummary === null` for a job
 * that hasn't reached HITL — so non-curator agents and pre-HITL jobs
 * render a clean subset rather than zeros and dashes.
 */
function JobSummaryCard({
  cost,
  review,
  wallClockMs,
}: {
  cost: JobCostSummary;
  review: JobReviewSummary | null;
  wallClockMs: number | null;
}) {
  const { agentsByType } = useAgents();
  const { modelsById } = useModels();
  const hasCost = cost.callCount > 0;
  const hasReview = review !== null;
  const hasWallClock = wallClockMs !== null;
  if (!hasCost && !hasReview && !hasWallClock) return null;

  return (
    <Card>
      <CardHeader>
        <p className="label-mono">Summary</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasCost ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Stat label="Cost" value={usd(cost.totalCostUsd)} />
            <Stat label="Calls" value={compact(cost.callCount)} />
            <Stat
              label="Tokens in / out"
              value={`${compact(cost.inputTokens)} / ${compact(cost.outputTokens)}`}
            />
            <Stat
              label="Cache reads"
              value={compact(cost.cacheReadInputTokens)}
              hint={`saved ${usd(cost.cacheSavingsUsd)}`}
            />
            <Stat label="Avg latency" value={ms(cost.avgLatencyMs)} />
            {hasWallClock ? (
              <Stat label="Wall clock" value={formatDurationMs(wallClockMs)} />
            ) : null}
          </div>
        ) : hasWallClock ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Stat label="Wall clock" value={formatDurationMs(wallClockMs)} />
          </div>
        ) : null}

        {hasCost && cost.modelIds.length > 0 ? (
          <div className="text-xs text-text-muted font-mono">
            <span className="label-mono mr-2">Models</span>
            {cost.modelIds.map((id, idx) => (
              <Fragment key={id}>
                {idx > 0 ? ", " : ""}
                <span title={id}>
                  {modelsById[id]?.displayName ?? id}
                </span>
              </Fragment>
            ))}
          </div>
        ) : null}

        {hasReview ? (
          <div className="flex flex-wrap gap-4">
            <Stat label="Approved" value={compact(review.approvedCount)} />
            <Stat label="Rejected" value={compact(review.rejectedCount)} />
          </div>
        ) : null}

        {hasCost && cost.byAgent.length > 1 ? (
          <table className="w-full text-sm font-mono">
            <thead>
              <tr className="text-left text-xs text-text-muted uppercase tracking-label">
                <th className="font-normal py-1 pr-3">Agent</th>
                <th className="font-normal py-1 pr-3">Attempts</th>
                <th className="font-normal py-1 pr-3">Calls</th>
                <th className="font-normal py-1 pr-3">Cost</th>
                <th className="font-normal py-1">Avg latency</th>
              </tr>
            </thead>
            <tbody>
              {cost.byAgent.map((row) => (
                <tr key={row.agentType} className="border-t border-border">
                  <td className="py-1 pr-3 text-text" title={row.agentType}>
                    {agentsByType[row.agentType]?.name ?? row.agentType}
                  </td>
                  <td className="py-1 pr-3">{compact(row.attemptCount)}</td>
                  <td className="py-1 pr-3">{compact(row.callCount)}</td>
                  <td className="py-1 pr-3">{usd(row.costUsd)}</td>
                  <td className="py-1">{ms(row.avgLatencyMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  hint,
  title,
}: {
  label: string;
  value: string;
  hint?: string;
  /**
   * Optional `title` attribute applied to the rendered value — used to
   * keep a forensic identifier (e.g. raw `model_id`) discoverable on
   * hover when the visible value is a friendly display name.
   */
  title?: string;
}) {
  return (
    <div>
      <p className="label-mono">{label}</p>
      <p className="font-mono text-lg text-text" title={title}>
        {value}
      </p>
      {hint ? <p className="text-xs text-text-muted font-mono mt-0.5">{hint}</p> : null}
    </div>
  );
}

/**
 * One `audit`-source timeline row. Visually mirrors `RunRow`'s
 * collapsed/expanded shape: collapsed by default, showing the
 * `eventType` plus a one-line truncated payload preview so the timeline
 * stays scannable; clicking reveals the full `payload` JSONB
 * pretty-printed in a bottom panel.
 *
 * Unlike `RunRow`, no fetch is needed on expand — the full payload
 * already arrives client-side in `job.auditLog`, so expand state lives
 * in local `useState` rather than being lifted to the parent. The
 * payload is
 * rendered as text content via `JSON.stringify` inside a `<pre>` (React
 * escapes it), per the admin-UI XSS-safe-rendering decision — no
 * `dangerouslySetInnerHTML`.
 *
 * Rows whose `payload` is `null` carry nothing to reveal, so they render
 * as a flat non-expandable `<li>` (no chevron, no toggle button).
 */
function AuditRow({ audit, timestamp }: { audit: AuditLogEntry; timestamp: string }) {
  const [expanded, setExpanded] = useState(false);
  const hasPayload = audit.payload != null;

  if (!hasPayload) {
    return (
      <li className="border border-border rounded bg-bg-card p-3 grid grid-cols-[24px_80px_1fr_auto] gap-3 items-center">
        <span aria-hidden="true" />
        <span className="label-mono !text-accent">audit</span>
        <div className="min-w-0">
          <div className="text-sm text-text">{audit.eventType}</div>
        </div>
        <span className="text-xs">
          <RelativeTime date={timestamp} />
        </span>
      </li>
    );
  }

  return (
    <li className="border border-border rounded bg-bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full grid grid-cols-[24px_80px_1fr_auto] gap-3 items-center p-3 text-left hover:bg-bg-card/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-expanded={expanded}
      >
        <span className="text-text-muted">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <span className="label-mono !text-accent">audit</span>
        <div className="min-w-0">
          <div className="text-sm text-text">{audit.eventType}</div>
          {expanded ? null : (
            <pre className="text-[11px] font-mono text-text-muted mt-1 truncate">
              {JSON.stringify(audit.payload)}
            </pre>
          )}
        </div>
        <span className="text-xs">
          <RelativeTime date={timestamp} />
        </span>
      </button>
      {expanded ? (
        <div className="border-t border-border p-3">
          <pre className="text-[11px] font-mono text-text-muted overflow-auto whitespace-pre-wrap break-words">
            {JSON.stringify(audit.payload, null, 2)}
          </pre>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Maps a per-call `stop_reason` to an operator-readable label.
 *
 * Only `tool_use` is a mid-loop pause — the model emitted a tool-use block
 * and the agent loop continued — so the bare `stopped: tool_use` wording
 * read as a termination when it is not. The other reasons (`end_turn`,
 * `max_tokens`, `stop_sequence`) are genuinely terminal, so the `stopped:`
 * shape is honest there. `stop_reason` is the closed `stopReasonSchema` enum
 * (`packages/messaging/src/types.ts`), but the admin-UI API types it as
 * `string | null`, so unknown values fall through to the terminal shape and
 * `null` renders `(unknown)`. The raw value stays one hover away via the
 * row's `title` attribute.
 */
function stopReasonLabel(stopReason: string | null): string {
  switch (stopReason) {
    case "tool_use":
      return "turn ended: tool_use → loop continued";
    case "end_turn":
      return "completed: end_turn";
    case null:
      return "stopped: (unknown)";
    default:
      return `stopped: ${stopReason}`;
  }
}

function RunRow({
  jobId,
  run,
  timestamp,
  expanded,
  onToggle,
}: {
  jobId: string;
  run: RunLogEntry;
  timestamp: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { agentsByType } = useAgents();
  // The `useRunCall` hook is gated on `expanded`; when the row is
  // collapsed it doesn't fetch. TanStack keeps the result cached after
  // the first expand so toggling on/off is free.
  const detail = useRunCall({
    jobId,
    agentType: run.agentType,
    callIndex: run.callIndex,
    enabled: expanded,
  });
  const agentLabel = agentsByType[run.agentType]?.name ?? run.agentType;

  return (
    <li className="border border-border rounded bg-bg-card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full grid grid-cols-[24px_80px_1fr_auto] gap-3 items-center p-3 text-left hover:bg-bg-card/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-expanded={expanded}
      >
        <span className="text-text-muted">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <span className="label-mono !text-emerald-300">run</span>
        <div className="min-w-0">
          <div className="text-sm text-text">
            <span
              className="font-mono text-xs text-accent"
              title={run.agentType}
            >
              {agentLabel} call #{run.callIndex}
            </span>{" "}
            {run.completedAt ? (
              <span className="text-text-muted" title={run.stopReason ?? "unknown"}>
                {stopReasonLabel(run.stopReason)}
              </span>
            ) : (
              <span className="text-text-muted">in flight</span>
            )}
          </div>
        </div>
        <span className="text-xs">
          <RelativeTime date={timestamp} />
        </span>
      </button>
      {expanded ? (
        <div className="border-t border-border p-3 space-y-3">
          {detail.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : detail.isError ? (
            <ErrorBanner
              error={detail.error}
              onRetry={() => void detail.refetch()}
            />
          ) : (
            <RunCallDetail data={detail.data} />
          )}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Expanded panel body for a single LLM call. Renders four sections in
 * order: a metrics strip (always shown when `llm_usage` joined), a
 * tool-call list (only when the call invoked at least one tool), the
 * full message array (collapsed by default), and the raw provider
 * response (collapsed by default). Messages and response are the
 * largest payloads so they sit behind toggles to keep the timeline
 * readable.
 */
function RunCallDetail({ data }: { data: RunCallDetailResponse }) {
  const [showMessages, setShowMessages] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  /** Default to the per-turn delta — re-rendering the full accumulated
   * conversation on every call gets unreadable past turn 2 or 3 of a
   * tool-using agent. The toggle exposes the full array when needed. */
  const [showFullMessages, setShowFullMessages] = useState(false);
  const { modelsById } = useModels();
  const toolCalls = parseToolUseBlocks(data.toolCalls);
  const toolResults = parseToolResultBlocks(data.toolResults);

  return (
    <>
      {data.modelId !== null && data.inputTokens !== null && data.outputTokens !== null ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Stat
            label="Model"
            value={modelsById[data.modelId]?.displayName ?? data.modelId}
            title={data.modelId}
          />
          <Stat
            label="Tokens in"
            value={compact(data.inputTokens)}
            hint={
              (data.cacheReadInputTokens ?? 0) > 0
                ? `${compact(data.cacheReadInputTokens!)} cache read`
                : undefined
            }
          />
          <Stat label="Tokens out" value={compact(data.outputTokens)} />
          {data.costUsd !== null ? <Stat label="Cost" value={usd(data.costUsd)} /> : null}
          {data.latencyMs !== null ? <Stat label="Latency" value={ms(data.latencyMs)} /> : null}
        </div>
      ) : null}

      {toolCalls.length > 0 ? (
        <section className="space-y-2">
          <p className="label-mono">Tool calls</p>
          <ul className="space-y-2">
            {toolCalls.map((tc) => {
              const result = toolResults.find((r) => r.toolUseId === tc.id);
              return (
                <li
                  key={tc.id}
                  className="border border-border/60 rounded bg-bg-dark/40 p-3 space-y-2"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-mono text-sm text-accent">{tc.name}</span>
                    <span className="text-xs font-mono text-text-muted">{tc.id}</span>
                  </div>
                  <div>
                    <p className="label-mono mb-1">Input</p>
                    <JsonBlock value={tc.input} />
                  </div>
                  {result ? (
                    <div>
                      <p className="label-mono mb-1">
                        Result{result.isError ? " (error)" : ""}
                      </p>
                      <JsonBlock value={result.content} />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <details
        className="group"
        open={showMessages}
        onToggle={(e) => setShowMessages(e.currentTarget.open)}
      >
        <summary className="cursor-pointer label-mono inline-flex items-center gap-1">
          <span className="group-open:rotate-90 transition-transform">
            <ChevronRight className="h-3 w-3" />
          </span>
          Messages
          <span className="ml-2 text-text-muted normal-case tracking-normal">
            {showFullMessages ? "(full conversation)" : "(this turn)"}
          </span>
        </summary>
        <div className="mt-2 space-y-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowFullMessages((v) => !v);
            }}
            className="text-xs font-mono text-text-muted hover:text-text underline-offset-2 hover:underline"
          >
            {showFullMessages
              ? "Show this turn only →"
              : "Show full conversation →"}
          </button>
          <JsonBlock
            value={showFullMessages ? data.messages : data.messagesDelta}
          />
        </div>
      </details>

      <details
        className="group"
        open={showResponse}
        onToggle={(e) => setShowResponse(e.currentTarget.open)}
      >
        <summary className="cursor-pointer label-mono inline-flex items-center gap-1">
          <span className="group-open:rotate-90 transition-transform">
            <ChevronRight className="h-3 w-3" />
          </span>
          Raw response
        </summary>
        <div className="mt-2">
          <JsonBlock value={data.response} />
        </div>
      </details>
    </>
  );
}

/**
 * Threshold (UTF-8 bytes) above which a single string leaf in a trace
 * payload is collapsed to a compact placeholder inside {@link JsonBlock}.
 * Sized well above typical leaf strings — tool inputs, message text, and
 * model ids are sub-KB to low-KB — and far below the 100 KB+ base64 blobs
 * an Anthropic `web_fetch_tool_result` `source.data` produces, so ordinary
 * payloads render in full and only the oversized fetched-page leaves
 * collapse. Single source of the threshold; tunable.
 */
const JSON_BLOCK_MAX_VALUE_BYTES = 8 * 1024;

/**
 * Deep-walk `value`, replacing any **string** leaf whose UTF-8 byte length
 * exceeds `maxBytes` with a compact placeholder of the form
 * `[truncated: <N> KB — show full]`, and return whether any replacement
 * happened.
 *
 * Walking string leaves (not whole subtrees) is deliberate: it shrinks the
 * one giant value — a base64-encoded PDF/image `source.data` or a long HTML
 * scrape round-tripped verbatim into `run_log.messages` / `run_log.response`
 * — while leaving the surrounding object structure (`url`, `type`,
 * `media_type` siblings) visible, which is the context an operator needs to
 * tell what the collapsed blob was. The byte length doubles as the "this is
 * the encrypted-looking wall of text you were worried about" affordance.
 *
 * Exported "for unit testing" mirroring the trace-block parsers above.
 */
export function truncateLargeValues(
  value: unknown,
  maxBytes: number,
): { value: unknown; truncated: boolean } {
  let truncated = false;
  const walk = (node: unknown): unknown => {
    if (typeof node === "string") {
      const bytes = new TextEncoder().encode(node).length;
      if (bytes > maxBytes) {
        truncated = true;
        return `[truncated: ${Math.round(bytes / 1024)} KB — show full]`;
      }
      return node;
    }
    if (Array.isArray(node)) return node.map(walk);
    if (node !== null && typeof node === "object") {
      return Object.fromEntries(
        Object.entries(node).map(([k, v]) => [k, walk(v)]),
      );
    }
    return node;
  };
  return { value: walk(value), truncated };
}

/** Pretty-printed JSON in a height-capped scrollable panel. Used for
 * messages, raw response, tool inputs, and tool results — all four are
 * arbitrary JSONB shapes that need a readable inline render without
 * ballooning the timeline. String leaves larger than
 * {@link JSON_BLOCK_MAX_VALUE_BYTES} render as a `[truncated: N KB — show
 * full]` placeholder behind a per-block toggle, so a run that fetched a
 * large PDF/HTML page stays scannable; the surrounding structure stays
 * visible. Exported "for unit testing" alongside the trace parsers. */
export function JsonBlock({ value }: { value: unknown }) {
  const [showFull, setShowFull] = useState(false);
  // Memoise the deep-walk so the per-render encode cost is paid only when
  // the payload changes, not on every toggle/parent re-render — the leaves
  // this targets are 100 KB+ base64 blobs.
  const { value: truncatedValue, truncated } = useMemo(
    () => truncateLargeValues(value, JSON_BLOCK_MAX_VALUE_BYTES),
    [value],
  );
  return (
    <div className="space-y-1">
      {truncated ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowFull((v) => !v);
          }}
          className="text-xs font-mono text-text-muted hover:text-text underline-offset-2 hover:underline"
        >
          {showFull ? "Collapse large values →" : "Show full →"}
        </button>
      ) : null}
      <pre className="text-[11px] font-mono text-text-muted bg-bg-dark/60 border border-border/60 rounded p-2 max-h-96 overflow-auto whitespace-pre-wrap break-words">
        {JSON.stringify(showFull ? value : truncatedValue, null, 2)}
      </pre>
    </div>
  );
}

export interface ParsedToolUse {
  id: string;
  name: string;
  input: unknown;
}

/**
 * Best-effort extraction of `tool_use` blocks from the JSONB payload
 * stored in `run_log.tool_calls`. The recorder writes Anthropic's raw
 * `tool_use` content blocks — `{ type: "tool_use", id, name, input }`.
 * Anything that doesn't match that shape is dropped silently rather
 * than failing the whole panel — the recorder is the only writer and
 * the JSONB shape is locked by `packages/messaging/`, so a malformed
 * row indicates a code-level shape change worth surfacing via tests
 * rather than a user-visible warning.
 *
 * Exported for unit testing.
 */
export function parseToolUseBlocks(value: unknown): ParsedToolUse[] {
  if (!Array.isArray(value)) return [];
  const out: ParsedToolUse[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    if (r.type !== "tool_use") continue;
    if (typeof r.id !== "string" || typeof r.name !== "string") continue;
    out.push({ id: r.id, name: r.name, input: r.input });
  }
  return out;
}

export interface ParsedToolResult {
  toolUseId: string;
  content: unknown;
  isError: boolean;
}

/** Mirror of {@link parseToolUseBlocks} for `run_log.tool_results`.
 * Exported for unit testing. */
export function parseToolResultBlocks(value: unknown): ParsedToolResult[] {
  if (!Array.isArray(value)) return [];
  const out: ParsedToolResult[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    if (r.type !== "tool_result") continue;
    if (typeof r.tool_use_id !== "string") continue;
    out.push({
      toolUseId: r.tool_use_id,
      content: r.content,
      isError: r.is_error === true,
    });
  }
  return out;
}

/**
 * Replaces {@link LiveIndicator} when a `validating` job carries the
 * validator-hop stall marker. Pulsing amber pip + "stalled" label —
 * deliberately distinct from {@link LiveIndicator}'s `errored` branch,
 * which renders a *static* amber dot for "stream disconnected." The
 * pulse signals "this job needs operator action" rather than "the SSE
 * stream blinked."
 */
function StalledIndicator() {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-mono text-amber-300">
      <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
      stalled
    </span>
  );
}

function LiveIndicator({ state }: { state: ReturnType<typeof useRunStream>["connectionState"] }) {
  if (state === "open") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono text-emerald-300">
        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
        live
      </span>
    );
  }
  if (state === "connecting" || state === "idle") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono text-text-muted">
        <span className="h-2 w-2 rounded-full bg-text-muted/50" />
        connecting…
      </span>
    );
  }
  if (state === "errored") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono text-amber-300">
        <span className="h-2 w-2 rounded-full bg-amber-400" />
        stream disconnected
      </span>
    );
  }
  return null;
}
